import { Controller } from "@hotwired/stimulus"

/**
 * Embed Image Controller
 *
 * Manages the embed image card: copy-to-clipboard for URL/HTML inputs,
 * alt text rows (add/remove/drag), instruction language warning, and save.
 *
 * Usage: Add data-controller="embed-image" to the embed image card, with
 * data-embed-image-alt-texts-url-value, data-embed-image-image-id-value,
 * data-embed-image-project-id-value (for project-scoped language list), 
 * data-embed-image-languages-value (JSON array), and targets: list, saveContainer,
 * instructionWarning, addRow, saveButton, status.
 */
export default class extends Controller {
  static targets = [
    "list",
    "saveContainer",
    "instructionWarning",
    "addRow",
    "saveButton",
    "status"
  ]

  static values = {
    altTextsUrl: String,
    imageId: Number,
    variantUuid: String,
    projectId: Number,
    languages: Array
  }

  connect() {
    this.originalAltTexts = {}
    this.originalOrder = []
    this.initCopyToClipboard()
    if (!this.hasListTarget || !this.hasSaveContainerTarget) return
    this.restoreLanguagesFromStorage()
    this.readOriginalAltTextState()
    this.updateSaveButtonVisibility()
    this.updateInstructionLangWarning()
    this.bindAddRow()
    this.bindSave()
    this.listTarget.querySelectorAll(".alt-text-row").forEach((row) => this.attachRowHandlers(row))
    this.bindDragAndDrop()
  }

  // --- Copy to clipboard ---
  initCopyToClipboard() {
    this.element.querySelectorAll(".input-group.readonly input").forEach((input) => {
      const value = input.getAttribute("data-value") || input.dataset.value
      if (value) input.value = value
      const groupText = input.closest(".input-group")?.querySelector(".input-group-text")
      if (groupText) {
        groupText.addEventListener("click", () => {
          input.select()
          document.execCommand("copy")
          const icon = groupText.querySelector("i")
          if (icon) {
            const originalClasses = icon.className
            icon.classList.remove("bi-clipboard")
            icon.classList.add("bi-check")
            setTimeout(() => { icon.className = originalClasses }, 1000)
          }
        })
      }
    })
  }

  // --- Shared languages (localStorage) ---
  // Use project scope so language selection is shared across all products in the project
  getStoredLanguagesKey() {
    const projectId = this.projectIdValue
    if (projectId) return `project_alt_text_languages_${projectId}`
    const uuid = this.variantUuidValue
    return uuid ? `variant_alt_text_languages_${uuid}` : null
  }

  getStoredLanguages() {
    const key = this.getStoredLanguagesKey()
    if (!key || typeof localStorage === "undefined") return []
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return []
      return JSON.parse(raw)
    } catch {
      return []
    }
  }

  addLanguageToStorage(lang) {
    if (!lang) return
    const key = this.getStoredLanguagesKey()
    if (!key || typeof localStorage === "undefined") return
    const list = this.getStoredLanguages()
    if (list.includes(lang)) return
    list.push(lang)
    list.sort()
    try {
      localStorage.setItem(key, JSON.stringify(list))
    } catch { }
  }

  restoreLanguagesFromStorage() {
    const stored = this.getStoredLanguages()
    const languages = this.languagesValue || []
    const existingLangs = []
    this.listTarget.querySelectorAll(".alt-text-row").forEach((row) => {
      const lang = row.querySelector(".alt-lang-select")?.value
      if (lang) existingLangs.push(lang)
    })
    stored.forEach((lang) => {
      if (existingLangs.includes(lang) || !languages.includes(lang)) return
      this.addRowWithLanguage(lang)
      existingLangs.push(lang)
    })
  }

  addRowWithLanguage(lang) {
    const languages = this.languagesValue || []
    const row = document.createElement("div")
    row.className = "alt-text-row mb-2"
    row.draggable = true
    row.style.cursor = "move"
    row.title = "Drag to reorder"
    row.innerHTML = `
      <div class="input-group">
        <select class="form-select alt-lang-select" style="max-width: 100px;">
          <option value="">--</option>
          ${languages.map((l) => `<option value="${l}" ${l === lang ? "selected" : ""}>${l.toUpperCase()}</option>`).join("")}
        </select>
        <input type="text" class="form-control alt-text-input" placeholder="Enter alt text for this language">
        <a href="#" class="input-group-text remove-row text-decoration-none" title="Remove" role="button">
          <i class="bi bi-trash text-danger"></i>
        </a>
      </div>
    `
    this.listTarget.appendChild(row)
    this.attachRowHandlers(row)
  }

  // --- Alt text state ---
  readOriginalAltTextState() {
    this.originalAltTexts = {}
    this.originalOrder = []
    this.listTarget.querySelectorAll(".alt-text-row").forEach((row) => {
      const lang = row.querySelector(".alt-lang-select")?.value
      const text = (row.querySelector(".alt-text-input")?.value || "").trim()
      if (lang) {
        this.originalAltTexts[lang] = text
        this.originalOrder.push(lang)
      }
    })
  }

  hasUnsavedChanges() {
    const rows = this.listTarget.querySelectorAll(".alt-text-row")
    const currentAltTexts = {}
    const currentOrder = []
    rows.forEach((row) => {
      const lang = row.querySelector(".alt-lang-select")?.value
      const text = (row.querySelector(".alt-text-input")?.value || "").trim()
      if (lang) {
        currentAltTexts[lang] = text
        currentOrder.push(lang)
      }
    })
    if (this.originalOrder.length !== currentOrder.length) return true
    if (this.originalOrder.join(",") !== currentOrder.join(",")) return true
    const currentKeys = Object.keys(currentAltTexts).sort().join(",")
    const originalKeys = Object.keys(this.originalAltTexts).sort().join(",")
    if (currentKeys !== originalKeys) return true
    for (const lang in currentAltTexts) {
      if (currentAltTexts[lang] !== this.originalAltTexts[lang]) return true
    }
    return false
  }

  updateSaveButtonVisibility() {
    const rows = this.listTarget.querySelectorAll(".alt-text-row")
    const hasRows = rows.length > 0
    this.saveContainerTarget.style.display = hasRows ? "block" : "none"
    this.updateInstructionLangWarning()
  }

  updateInstructionLangWarning() {
    if (!this.hasInstructionWarningTarget) return
    const warning = this.instructionWarningTarget
    const instructionLang = warning.getAttribute("data-instruction-lang")
    if (!instructionLang) {
      warning.style.display = "none"
      return
    }
    const rows = this.listTarget.querySelectorAll(".alt-text-row")
    if (rows.length === 0) {
      warning.style.display = "none"
      return
    }
    let hasInstructionLang = false
    rows.forEach((row) => {
      const langSelect = row.querySelector(".alt-lang-select")
      const textInput = row.querySelector(".alt-text-input")
      if (langSelect?.value === instructionLang && textInput?.value?.trim()) {
        hasInstructionLang = true
      }
    })
    if (!hasInstructionLang) {
      warning.querySelectorAll("#instruction_lang_name, #instruction_lang_name2").forEach((el) => {
        el.textContent = instructionLang.toUpperCase()
      })
      warning.style.display = "block"
    } else {
      warning.style.display = "none"
    }
  }

  // --- Add row (action) ---
  bindAddRow() {
    if (!this.hasAddRowTarget) return
    this.addRowTarget.addEventListener("click", (e) => {
      e.preventDefault()
      this.addRow()
    })
  }

  getUsedLanguages() {
    const used = new Set()
    this.listTarget.querySelectorAll(".alt-text-row").forEach((row) => {
      const lang = row.querySelector(".alt-lang-select")?.value
      if (lang) used.add(lang)
    })
    return used
  }

  addRow() {
    const languages = this.languagesValue || []
    const used = this.getUsedLanguages()
    const firstUnused = languages.find((l) => !used.has(l))
    const row = document.createElement("div")
    row.className = "alt-text-row mb-2"
    row.draggable = true
    row.style.cursor = "move"
    row.title = "Drag to reorder"
    row.innerHTML = `
      <div class="input-group">
        <select class="form-select alt-lang-select" style="max-width: 100px;">
          <option value="">--</option>
          ${languages.map((lang) => `<option value="${lang}" ${lang === firstUnused ? "selected" : ""}>${lang.toUpperCase()}</option>`).join("")}
        </select>
        <input type="text" class="form-control alt-text-input" placeholder="Enter alt text for this language">
        <a href="#" class="input-group-text remove-row text-decoration-none" title="Remove" role="button">
          <i class="bi bi-trash text-danger"></i>
        </a>
      </div>
    `
    this.listTarget.appendChild(row)
    this.attachRowHandlers(row)
    this.updateSaveButtonVisibility()
  }

  removeRow(event) {
    event.preventDefault()
    event.stopPropagation()
    const row = event.target.closest(".alt-text-row")
    if (row) {
      row.remove()
      this.updateSaveButtonVisibility()
    }
  }

  attachRowHandlers(row) {
    const removeLink = row.querySelector(".remove-row")
    if (removeLink) {
      removeLink.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        row.remove()
        this.updateSaveButtonVisibility()
      })
    }
    const langSelect = row.querySelector(".alt-lang-select")
    const textInput = row.querySelector(".alt-text-input")
    if (langSelect) {
      langSelect.addEventListener("change", () => {
        this.addLanguageToStorage(langSelect.value)
        this.updateSaveButtonVisibility()
      })
    }
    ;[langSelect, textInput].forEach((el) => {
      if (el) {
        el.addEventListener("input", () => this.updateSaveButtonVisibility())
        if (el !== langSelect) el.addEventListener("change", () => this.updateSaveButtonVisibility())
      }
    })
  }

  // --- Drag and drop ---
  bindDragAndDrop() {
    let draggedElement = null
    this.listTarget.addEventListener("dragstart", (e) => {
      if (
        e.target.closest(".alt-text-row") &&
        !e.target.closest("select") &&
        !e.target.closest("input") &&
        !e.target.closest("a")
      ) {
        draggedElement = e.target.closest(".alt-text-row")
        draggedElement.classList.add("dragging")
        e.dataTransfer.effectAllowed = "move"
      } else {
        e.preventDefault()
      }
    })
    this.listTarget.addEventListener("dragover", (e) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      const afterElement = this.getDragAfterElement(this.listTarget, e.clientY)
      const dragging = this.listTarget.querySelector(".dragging")
      if (!dragging) return
      if (afterElement == null) {
        this.listTarget.appendChild(dragging)
      } else {
        this.listTarget.insertBefore(dragging, afterElement)
      }
    })
    this.listTarget.addEventListener("dragend", () => {
      const dragging = this.listTarget.querySelector(".dragging")
      if (dragging) {
        dragging.classList.remove("dragging")
        this.updateSaveButtonVisibility()
      }
    })
  }

  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll(".alt-text-row:not(.dragging)")]
    return draggableElements.reduce(
      (closest, child) => {
        const box = child.getBoundingClientRect()
        const offset = y - box.top - box.height / 2
        if (offset < 0 && offset > closest.offset) {
          return { offset, element: child }
        }
        return closest
      },
      { offset: Number.NEGATIVE_INFINITY }
    ).element
  }

  // --- Save (action) ---
  bindSave() {
    if (!this.hasSaveButtonTarget) return
    this.saveButtonTarget.addEventListener("click", () => this.save())
  }

  save() {
    const rows = this.listTarget.querySelectorAll(".alt-text-row")
    const payload = {}
    rows.forEach((row) => {
      const select = row.querySelector(".alt-lang-select")
      const input = row.querySelector(".alt-text-input")
      const lang = select?.value?.trim()
      const text = (input?.value ?? "").trim()
      if (lang && text) payload[lang] = text
    })

    const button = this.hasSaveButtonTarget ? this.saveButtonTarget : null
    if (button) button.disabled = true
    if (this.hasStatusTarget) this.statusTarget.textContent = ""

    const url = this.altTextsUrlValue
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json"
      },
      body: JSON.stringify({ alt_texts: payload })
    })
      .then((response) => {
        if (!response.ok) throw new Error("Save failed")
        return response.json()
      })
      .then((response) => {
        if (this.hasStatusTarget) this.statusTarget.textContent = ""
        this.originalAltTexts = payload
        this.originalOrder = Object.keys(payload)
        const firstLang = this.originalOrder[0]
        const altValue = payload[firstLang] || ""
        const htmlInput = this.element.querySelector('input[data-value*="alt="]')
        if (htmlInput) {
          const currentValue = htmlInput.getAttribute("data-value")
          const newValue = currentValue.replace(
            /alt="[^"]*"/,
            `alt="${altValue ? altValue.replace(/"/g, "&quot;") : ""}"`
          )
          htmlInput.setAttribute("data-value", newValue)
          htmlInput.value = newValue
        }
        this.updateSaveButtonVisibility()
        Object.keys(payload).forEach((lang) => this.addLanguageToStorage(lang))
        this.showDefaultToast("Alt text saved")
        document.dispatchEvent(
          new CustomEvent("altTextUpdated", {
            detail: { imageId: this.imageIdValue, altTexts: response.alt_texts || {} }
          })
        )
      })
      .catch((err) => {
        if (this.hasStatusTarget) this.statusTarget.textContent = "Error saving alt text"
        console.error("Alt text save failed", err)
      })
      .finally(() => {
        if (button) button.disabled = false
      })
  }

  // Use the app default toast: append to #toasts with same markup as shared/_toast.html.erb (success style).
  // The toast controller will connect and show it.
  showDefaultToast(message) {
    const container = document.getElementById("toasts")
    if (!container) return
    const toast = document.createElement("div")
    toast.className = "toast"
    toast.setAttribute("data-controller", "toast")
    toast.setAttribute("role", "alert")
    toast.setAttribute("aria-live", "assertive")
    toast.setAttribute("aria-atomic", "true")
    toast.setAttribute("data-bs-autohide", "true")
    toast.setAttribute("data-bs-delay", "8000")
    toast.style.minWidth = "20em"
    toast.style.marginBottom = "0.5em"
    toast.style.border = "none !important"
    toast.style.backgroundColor = "#fff"
    toast.style.boxShadow = "none !important"
    toast.innerHTML = `
      <div class="toast-body" style="font-size: 0.875rem;">
        <span style="color: #28a745; font-weight: 500;">${message.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</span>
      </div>
    `
    container.appendChild(toast)
  }
}
